import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { DeleteConfirmationModal } from "../components/ui/delete-confirmation-modal";
import { 
  Users,
  Search,
  Plus,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Trash2,
  Edit,
  MoreVertical,
  RotateCcw
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import AdminSidebar from "../components/layout/AdminSidebar";

const OwnersPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [owners, setOwners] = useState([]);
  const [archivedOwners, setArchivedOwners] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [archivedSearchQuery, setArchivedSearchQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("owners");
  
  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [relatedRecords, setRelatedRecords] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Permanent delete state
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [confirmationName, setConfirmationName] = useState("");
  const [permanentDeleting, setPermanentDeleting] = useState(false);
  
  const [newOwner, setNewOwner] = useState({
    first_name: "",
    last_name: "",
    email_primary: "",
    email_secondary: "",
    phone_primary: "",
    phone_secondary: "",
    mailing_address: "",
    preferred_contact_method: "email",
    status: "active",
    notes: ""
  });

  const fetchOwners = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/owners?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data);
    } catch (error) {
      toast.error("Failed to load owners");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedOwners = async () => {
    try {
      const params = new URLSearchParams();
      if (archivedSearchQuery) params.append("search", archivedSearchQuery);
      
      const response = await axios.get(`${API}/owners/archived/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedOwners(response.data);
    } catch (error) {
      console.error("Failed to load archived owners:", error);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/owners/property-claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data);
    } catch (error) {
      console.error("Failed to load claims:", error);
    }
  };

  useEffect(() => {
    fetchOwners();
    fetchClaims();
    fetchArchivedOwners();
  }, [token, searchQuery]);

  useEffect(() => {
    if (activeTab === "archived") {
      fetchArchivedOwners();
    }
  }, [archivedSearchQuery, activeTab]);

  // Restore archived owner
  const handleRestoreOwner = async (owner) => {
    try {
      await axios.post(`${API}/owners/${owner.owner_id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${owner.name} has been restored`);
      fetchOwners();
      fetchArchivedOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore owner");
    }
  };

  // Permanent delete handler
  const handlePermanentDeleteClick = (owner) => {
    setSelectedOwner(owner);
    setConfirmationName("");
    setPermanentDeleteOpen(true);
  };

  const handlePermanentDeleteConfirm = async () => {
    if (!selectedOwner) return;
    
    const fullName = selectedOwner.name || `${selectedOwner.first_name} ${selectedOwner.last_name}`.trim();
    if (confirmationName.trim().toLowerCase() !== fullName.toLowerCase()) {
      toast.error(`Please type "${fullName}" to confirm deletion`);
      return;
    }
    
    setPermanentDeleting(true);
    try {
      const response = await axios.delete(
        `${API}/owners/${selectedOwner.owner_id}/permanent?confirmation_name=${encodeURIComponent(confirmationName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setPermanentDeleteOpen(false);
      setSelectedOwner(null);
      setConfirmationName("");
      fetchOwners();
      fetchArchivedOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to permanently delete owner");
    } finally {
      setPermanentDeleting(false);
    }
  };

  const handleApproveClaim = async (claimId) => {
    try {
      await axios.post(`${API}/owners/property-claims/${claimId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property claim approved");
      fetchClaims();
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve claim");
    }
  };

  const handleDenyClaim = async (claimId) => {
    const reason = prompt("Enter reason for denial (optional):");
    try {
      await axios.post(`${API}/owners/property-claims/${claimId}/deny`, null, {
        params: { reason: reason || "Denied by board" },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property claim denied");
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny claim");
    }
  };

  const pendingClaimsCount = claims.filter(c => c.status === "pending").length;

  // Delete owner handlers
  const handleDeleteClick = async (owner) => {
    setSelectedOwner(owner);
    try {
      const response = await axios.get(`${API}/owners/${owner.owner_id}/related-records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRelatedRecords(response.data);
    } catch (error) {
      console.error("Failed to check related records:", error);
      setRelatedRecords(null);
    }
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedOwner) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/owners/${selectedOwner.owner_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(relatedRecords?.has_related_records 
        ? "Owner archived (soft deleted)" 
        : "Owner deleted");
      setDeleteOpen(false);
      setSelectedOwner(null);
      setRelatedRecords(null);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete owner");
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newOwner.first_name || !newOwner.last_name || !newOwner.email_primary) {
      toast.error("Please fill in required fields (First Name, Last Name, Email)");
      return;
    }

    try {
      await axios.post(`${API}/owners`, newOwner, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Owner created");
      setCreateOpen(false);
      setNewOwner({ 
        first_name: "", last_name: "", email_primary: "", email_secondary: "",
        phone_primary: "", phone_secondary: "", mailing_address: "",
        preferred_contact_method: "email", status: "active", notes: ""
      });
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create owner");
    }
  };

  const content = (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page Title */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-chart-2/10 flex items-center justify-center">
          <Users className="w-5 h-5 text-chart-2" />
        </div>
        <div>
          <span className="font-serif text-lg font-semibold block">Owners</span>
          <span className="text-xs text-muted-foreground">Manage property owners and claims</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="owners">Owners ({owners.length})</TabsTrigger>
          <TabsTrigger value="claims" className="relative">
            Property Claims
            {pendingClaimsCount > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white text-xs">{pendingClaimsCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="archived" className="relative">
            Archived
            {archivedOwners.length > 0 && (
              <Badge className="ml-2 bg-muted text-muted-foreground text-xs">{archivedOwners.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="owners">
          {/* Search and Create */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-owners-input"
              />
              </div>
              
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-owner-btn" className="bg-chart-2 text-white hover:bg-chart-2/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Owner
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif">Add Owner</DialogTitle>
                <DialogDescription>Register a new property owner</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={newOwner.first_name}
                      onChange={(e) => setNewOwner({ ...newOwner, first_name: e.target.value })}
                      placeholder="John"
                      data-testid="owner-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={newOwner.last_name}
                      onChange={(e) => setNewOwner({ ...newOwner, last_name: e.target.value })}
                      placeholder="Smith"
                      data-testid="owner-lastname-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Email *</Label>
                    <Input
                      type="email"
                      value={newOwner.email_primary}
                      onChange={(e) => setNewOwner({ ...newOwner, email_primary: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="owner-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Email</Label>
                    <Input
                      type="email"
                      value={newOwner.email_secondary}
                      onChange={(e) => setNewOwner({ ...newOwner, email_secondary: e.target.value })}
                      placeholder="john.work@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Phone</Label>
                    <Input
                      value={newOwner.phone_primary}
                      onChange={(e) => setNewOwner({ ...newOwner, phone_primary: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="owner-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Phone</Label>
                    <Input
                      value={newOwner.phone_secondary}
                      onChange={(e) => setNewOwner({ ...newOwner, phone_secondary: e.target.value })}
                      placeholder="(555) 987-6543"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Mailing Address</Label>
                  <Input
                    value={newOwner.mailing_address}
                    onChange={(e) => setNewOwner({ ...newOwner, mailing_address: e.target.value })}
                    placeholder="123 Main St, City, State 12345"
                    data-testid="owner-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Preferred Contact</Label>
                    <Select
                      value={newOwner.preferred_contact_method}
                      onValueChange={(v) => setNewOwner({ ...newOwner, preferred_contact_method: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="mail">Mail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={newOwner.status}
                      onValueChange={(v) => setNewOwner({ ...newOwner, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="delinquent">Delinquent</SelectItem>
                        <SelectItem value="under_review">Under Review</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Internal Notes</Label>
                  <Input
                    value={newOwner.notes}
                    onChange={(e) => setNewOwner({ ...newOwner, notes: e.target.value })}
                    placeholder="Board notes about this owner (not visible to owner)"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="owner-submit-btn">Add Owner</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Owners Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading owners...</div>
        ) : owners.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No owners found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                Add your first owner
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {owners.map((owner) => (
              <Card key={owner.owner_id} data-testid={`owner-${owner.owner_id}`} className="hover:shadow-floating transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-chart-2/10 flex items-center justify-center text-chart-2 font-semibold text-lg">
                      {owner.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg truncate">{owner.name}</h3>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/board/owners/${owner.owner_id}`)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleDeleteClick(owner)}
                              data-testid={`delete-owner-${owner.owner_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{owner.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t space-y-2">
                    {owner.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        {owner.phone}
                      </div>
                    )}
                    {owner.mailing_address && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{owner.mailing_address}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4" />
                      <span className={owner.dues_balance > 0 ? "text-destructive" : "text-green-600"}>
                        ${owner.dues_balance?.toFixed(2) || "0.00"} balance
                      </span>
                    </div>
                    {owner.property_ids?.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {owner.property_ids.length} property(ies)
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="claims">
            <Card>
              <CardHeader>
                <CardTitle>Property Claim Requests</CardTitle>
                <CardDescription>Review and approve owner requests to link properties to their accounts</CardDescription>
              </CardHeader>
              <CardContent>
                {claims.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No property claims</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {claims.map((claim) => (
                      <div key={claim.claim_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold">{claim.owner_name}</p>
                              {claim.status === "pending" && (
                                <Badge className="bg-yellow-500/10 text-yellow-600">
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </Badge>
                              )}
                              {claim.status === "approved" && (
                                <Badge className="bg-green-500/10 text-green-600">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Approved
                                </Badge>
                              )}
                              {claim.status === "denied" && (
                                <Badge className="bg-red-500/10 text-red-600">
                                  <XCircle className="w-3 h-3 mr-1" /> Denied
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              <Building2 className="w-4 h-4 inline mr-1" />
                              Lot {claim.lot_number} - {claim.property_address}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Submitted: {new Date(claim.created_at).toLocaleDateString()}
                            </p>
                            {claim.denial_reason && (
                              <p className="text-sm text-red-600 mt-1">Reason: {claim.denial_reason}</p>
                            )}
                          </div>
                          {claim.status === "pending" && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDenyClaim(claim.claim_id)}
                                data-testid={`deny-claim-${claim.claim_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Deny
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => handleApproveClaim(claim.claim_id)}
                                data-testid={`approve-claim-${claim.claim_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Archived Owners Tab */}
          <TabsContent value="archived">
            <Card>
              <CardHeader>
                <CardTitle>Archived Owners</CardTitle>
                <CardDescription>
                  Owners that have been soft-deleted. You can restore them or permanently delete them (Admin only).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search archived owners..."
                    value={archivedSearchQuery}
                    onChange={(e) => setArchivedSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-archived-owners-input"
                  />
                </div>

                {archivedOwners.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No archived owners</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {archivedOwners.map((owner) => (
                      <div key={owner.owner_id} className="p-4 border rounded-lg bg-muted/30">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold">{owner.name}</p>
                              <Badge variant="outline" className="text-muted-foreground">
                                Archived
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              {owner.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {owner.email}
                                </span>
                              )}
                              {owner.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {owner.phone}
                                </span>
                              )}
                            </div>
                            {owner.deleted_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Archived: {new Date(owner.deleted_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestoreOwner(owner)}
                              data-testid={`restore-owner-${owner.owner_id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                            {user?.role === "admin" && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handlePermanentDeleteClick(owner)}
                                data-testid={`permanent-delete-${owner.owner_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete Forever
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Owner"
        description="Are you sure you want to delete this owner? This action will archive the owner record."
        itemName={selectedOwner?.name}
        itemType="Owner"
        warningMessage={relatedRecords?.warning_message}
        relatedRecords={relatedRecords}
        onConfirm={handleDeleteConfirm}
        isLoading={deleting}
        deleteButtonText={relatedRecords?.has_related_records ? "Archive Owner" : "Delete Owner"}
      />

      {/* Permanent Delete Confirmation Modal */}
      <Dialog open={permanentDeleteOpen} onOpenChange={setPermanentDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive font-serif flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Permanently Delete Owner
            </DialogTitle>
            <DialogDescription>
              This action is <strong>irreversible</strong>. The owner record will be permanently removed and cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm">
              <p className="font-semibold text-destructive mb-2">⚠️ Warning: This will:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Permanently delete the owner record</li>
                <li>Remove owner association from all properties</li>
                <li>Clear owner references from violations, DRC submissions, and payments</li>
                <li>This cannot be undone</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>
                Type <strong>{selectedOwner?.name || `${selectedOwner?.first_name} ${selectedOwner?.last_name}`.trim()}</strong> to confirm:
              </Label>
              <Input
                value={confirmationName}
                onChange={(e) => setConfirmationName(e.target.value)}
                placeholder="Enter owner's full name"
                data-testid="permanent-delete-confirmation-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPermanentDeleteOpen(false);
                setConfirmationName("");
              }}
              disabled={permanentDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handlePermanentDeleteConfirm}
              disabled={permanentDeleting || !confirmationName}
              data-testid="confirm-permanent-delete-btn"
            >
              {permanentDeleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return <AdminSidebar>{content}</AdminSidebar>;
};

export default OwnersPage;
